/* src/modules/hr/OrgChart.js */
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { ChevronDownIcon, ChevronUpIcon, ExclamationIcon, BriefcaseIcon } from '../../components/icons/Icons';

export default function OrgChart({ appId }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    const FOUNDER_EMAILS = [
        'abhinav@linceducation.com', 
        'amit@linceducation.com', 
        'pranav@linceducation.com'
    ];

    // ✅ DUAL ROLE CONFIGURATION
    const DUAL_ROLE_USER_EMAIL = 'vartikka@linceducation.com';
    
    const DUAL_ROLE_CONFIG = {
        roleA: {
            managerEmail: 'abhinav@linceducation.com',
            domain: 'linceducation.com',
            suffix: '(LINC)' 
        },
        roleB: {
            managerEmail: 'pranav@linceducation.com',
            domain: 'theworldgrad.com',
            suffix: '(TWG)'
        }
    };

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            try {
                const q = query(
                    collection(db, `artifacts/${appId}/users`),
                    where("status", "==", "Approved")
                );
                const snapshot = await getDocs(q);
                const userData = snapshot.docs.map(doc => ({ 
                    id: doc.id, 
                    ...doc.data(),
                    email: doc.data().email ? doc.data().email.toLowerCase().trim() : ''
                }));
                setUsers(userData);
            } catch (error) {
                console.error("Error fetching org chart data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
    }, [appId]);

    // --- HIERARCHY BUILDER ---
    const { founderTrees, unassignedNodes } = useMemo(() => {
        if (!users.length) return { founderTrees: [], unassignedNodes: [] };

        const userMap = {};
        const founderNodes = [];
        const unassigned = [];

        // 1. Initialize Map
        users.forEach(user => {
            userMap[user.id] = { ...user, children: [] };
        });

        // --- DUAL ROLE LOGIC ---
        const dualUser = users.find(u => u.email === DUAL_ROLE_USER_EMAIL);
        let vartikaNodeA = null;
        let vartikaNodeB = null;

        if (dualUser) {
            vartikaNodeA = { 
                ...dualUser, 
                id: `${dualUser.id}-LINC`, 
                name: `${dualUser.name} ${DUAL_ROLE_CONFIG.roleA.suffix}`,
                children: [] 
            };
            vartikaNodeB = { 
                ...dualUser, 
                id: `${dualUser.id}-TWG`, 
                name: `${dualUser.name} ${DUAL_ROLE_CONFIG.roleB.suffix}`,
                children: [] 
            };
        }

        // 2. Identify Founders
        FOUNDER_EMAILS.forEach(email => {
            const founder = users.find(u => u.email === email);
            if (founder) founderNodes.push(userMap[founder.id]);
        });

        // 3. Link Reportees
        users.forEach(user => {
            if (FOUNDER_EMAILS.includes(user.email)) return;
            if (user.email === DUAL_ROLE_USER_EMAIL) return;

            const node = userMap[user.id];

            if (dualUser && user.managerId === dualUser.id) {
                if (user.email.includes(DUAL_ROLE_CONFIG.roleB.domain)) {
                    vartikaNodeB.children.push(node);
                } else {
                    vartikaNodeA.children.push(node);
                }
                return; 
            }

            if (user.managerId && userMap[user.managerId]) {
                userMap[user.managerId].children.push(node);
            } else {
                unassigned.push(node);
            }
        });

        // 4. Attach Vartika's Virtual Nodes
        if (dualUser) {
            const managerA = users.find(u => u.email === DUAL_ROLE_CONFIG.roleA.managerEmail);
            if (managerA && userMap[managerA.id]) userMap[managerA.id].children.push(vartikaNodeA);

            const managerB = users.find(u => u.email === DUAL_ROLE_CONFIG.roleB.managerEmail);
            if (managerB && userMap[managerB.id]) userMap[managerB.id].children.push(vartikaNodeB);
        }

        // 5. GROUP BY DEPARTMENT (Founders Only)
        founderNodes.forEach(founder => {
            if (founder.children.length > 0) {
                const deptMap = {};
                founder.children.forEach(child => {
                    const deptName = child.department || 'General';
                    if (!deptMap[deptName]) deptMap[deptName] = [];
                    deptMap[deptName].push(child);
                });

                const groupedChildren = Object.keys(deptMap).map(deptName => ({
                    id: `dept-${founder.id}-${deptName}`, 
                    type: 'DEPARTMENT', 
                    name: deptName,
                    children: deptMap[deptName]
                }));

                founder.children = groupedChildren;
            }
        });

        return { founderTrees: founderNodes, unassignedNodes: unassigned };
    }, [users]);

    if (loading) return <LoadingSpinner />;
    if (!users.length) return <div className="text-center py-10 text-gray-500">No active employees found.</div>;

    return (
        <div className="overflow-auto p-8 bg-gray-50 rounded-xl border border-gray-200 min-h-[600px] shadow-inner">
            <div className="min-w-max flex flex-col items-center pb-20">
                {/* FOUNDERS ROW */}
                <div className="flex space-x-16 items-start justify-center">
                    {founderTrees.map((founder) => (
                        <div key={founder.id} className="flex flex-col items-center">
                            <TreeNode node={founder} depth={0} isFounder={true} />
                        </div>
                    ))}
                </div>

                {/* UNASSIGNED ROW */}
                {unassignedNodes.length > 0 && (
                    <div className="mt-24 pt-8 border-t-2 border-dashed border-gray-300 w-full flex flex-col items-center">
                        <div className="flex items-center text-gray-500 mb-6 bg-white px-4 py-2 rounded-full shadow-sm">
                            <ExclamationIcon className="h-5 w-5 mr-2 text-orange-500" />
                            <span className="font-bold text-sm">Unassigned / Manager Not Found ({unassignedNodes.length})</span>
                        </div>
                        <div className="flex flex-wrap justify-center gap-8 max-w-5xl">
                            {unassignedNodes.map(node => (
                                <SimpleNode key={node.id} node={node} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// --- RECURSIVE MEMBER COUNTER ---
const getMemberCount = (node) => {
    if (!node.children || node.children.length === 0) return 0;
    let count = 0;
    node.children.forEach(child => {
        count += 1 + getMemberCount(child);
    });
    return count;
};

// --- TREE NODE RENDERER ---
function TreeNode({ node, depth, isFounder }) {
    const isDepartment = node.type === 'DEPARTMENT';
    const [isExpanded, setIsExpanded] = useState(isDepartment ? false : true);
    const hasChildren = node.children && node.children.length > 0;
    
    const handleToggle = (e) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    // DEPARTMENT NODE
    if (isDepartment) {
        const totalMembers = getMemberCount(node);
        const nameLength = node.name.length;
        const textSizeClass = nameLength > 20 ? 'text-[10px]' : nameLength > 12 ? 'text-xs' : 'text-sm';

        return (
            <div className="flex flex-col items-center">
                <div onClick={handleToggle} className={`relative z-10 flex flex-col items-center justify-center w-40 h-24 p-2 bg-white rounded-lg border shadow-sm transition-all duration-200 cursor-pointer hover:shadow-md mb-12 ${isExpanded ? 'border-blue-400 ring-1 ring-blue-100' : 'border-gray-200'}`}>
                    <div className="absolute -top-12 left-1/2 -ml-px h-12 w-px bg-gray-300"></div>
                    <div className="bg-blue-50 p-1.5 rounded-full text-blue-600 mb-1"><BriefcaseIcon className="h-4 w-4" /></div>
                    <h3 className={`${textSizeClass} font-bold text-gray-700 uppercase tracking-wide text-center leading-tight break-words w-full px-1`}>{node.name}</h3>
                    <span className="mt-1 text-[10px] text-gray-400 font-bold bg-gray-50 px-2 py-0.5 rounded-full">{totalMembers} People</span>
                    <div className="absolute -bottom-3 bg-white rounded-full border border-gray-200 p-0.5 text-gray-400">
                        {isExpanded ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />}
                    </div>
                </div>
                {isExpanded && hasChildren && <ChildrenContainer node={node} depth={depth} />}
            </div>
        );
    }

    // USER NODE
    return (
        <div className="flex flex-col items-center">
            <div className={`relative z-10 flex flex-col items-center w-48 transition-all duration-200 mb-12 ${hasChildren ? 'cursor-pointer group' : ''}`} onClick={hasChildren ? handleToggle : undefined}>
                {!isFounder && <div className="absolute -top-12 left-1/2 -ml-px h-12 w-px bg-gray-300"></div>}
                
                <div className={`relative p-1 rounded-full bg-white ${isFounder ? 'border-2 border-blue-100' : hasChildren ? 'border-2 border-transparent group-hover:border-[#20BEC6]' : ''}`}>
                    <img src={node.photoURL && node.photoURL.length > 10 ? node.photoURL : `https://ui-avatars.com/api/?name=${node.name}&background=random`} alt={node.name} className="w-14 h-14 rounded-full object-cover shadow-sm" onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${node.name}&background=random`; }} />
                    {hasChildren && (
                        <div className="absolute bottom-0 right-0 bg-white rounded-full border border-gray-200 p-0.5 text-gray-400 shadow-sm">
                            {isExpanded ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />}
                        </div>
                    )}
                </div>
                
                <div className="mt-2 text-center bg-white/80 backdrop-blur-sm px-2 py-1 rounded-lg">
                    <h3 className={`text-sm font-bold leading-tight truncate w-full ${isFounder ? 'text-blue-900' : 'text-[#213368]'}`}>{node.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5 font-medium truncate w-full">{node.designation || 'Team Member'}</p>
                </div>
            </div>
            {isExpanded && hasChildren && <ChildrenContainer node={node} depth={depth} />}
        </div>
    );
}

function ChildrenContainer({ node, depth }) {
    return (
        <div className="flex flex-col items-center animate-fadeIn">
            {node.children.length > 1 && <div className="relative h-px bg-gray-300 w-full mb-0"></div>}
            <div className="flex justify-center items-start pt-0 space-x-4">
                {node.children.map((child, index) => {
                    const isFirst = index === 0;
                    const isLast = index === node.children.length - 1;
                    const isOnly = node.children.length === 1;
                    return (
                        <div key={child.id} className="flex flex-col items-center relative">
                            {!isOnly && (
                                <>
                                    <div className={`absolute top-[-1px] right-1/2 h-px bg-gray-300 ${isFirst ? 'w-0' : 'w-1/2'}`}></div>
                                    <div className={`absolute top-[-1px] left-1/2 h-px bg-gray-300 ${isLast ? 'w-0' : 'w-1/2'}`}></div>
                                </>
                            )}
                            <TreeNode node={child} depth={depth + 1} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function SimpleNode({ node }) {
    return (
        <div className="flex flex-col items-center w-32 opacity-75 hover:opacity-100 transition">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden mb-2">
                <img src={node.photoURL || `https://ui-avatars.com/api/?name=${node.name}&background=random`} alt={node.name} className="w-full h-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${node.name}&background=random`; }} />
            </div>
            <p className="text-xs font-bold text-gray-700 text-center truncate w-full">{node.name}</p>
            <p className="text-[10px] text-red-400">Unassigned</p>
        </div>
    );
}